/**
 * Life4Earth V2 — Project Phases API Adapter
 *
 * Endpoint: /api/projectphases?projectId={guid}
 *
 * Standalone phases for Standard-tier projects.
 * In Professional tier, these merge with Gantt-sourced phases.
 *
 * Epic: DEV-786 (Standard/Professional tier split)
 */

import { apiFetch } from './client';

// ── API Response Types ─────────────────────────────────────

export interface ProjectPhaseResponse {
  id: string;
  projectId: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  percentComplete: number;
  sortOrder: number;
  source: string;
  ganttTaskId: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Request Types ──────────────────────────────────────────

export interface CreatePhaseRequest {
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  percentComplete?: number;
  sortOrder?: number;
}

export interface UpdatePhaseRequest {
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  percentComplete?: number;
  sortOrder?: number;
}

// ── API Functions ──────────────────────────────────────────

/**
 * List all standalone phases for a project.
 */
export async function fetchProjectPhases(
  projectGuid: string
): Promise<ProjectPhaseResponse[]> {
  return apiFetch<ProjectPhaseResponse[]>(
    `/api/projectphases?projectId=${encodeURIComponent(projectGuid)}`
  );
}

/**
 * Create a new phase.
 */
export async function createProjectPhase(
  projectGuid: string,
  req: CreatePhaseRequest
): Promise<ProjectPhaseResponse> {
  return apiFetch<ProjectPhaseResponse>(
    `/api/projectphases?projectId=${encodeURIComponent(projectGuid)}`,
    {
      method: 'POST',
      body: JSON.stringify(req),
    }
  );
}

/**
 * Update an existing phase (partial).
 */
export async function updateProjectPhase(
  phaseId: string,
  req: UpdatePhaseRequest
): Promise<ProjectPhaseResponse> {
  return apiFetch<ProjectPhaseResponse>(
    `/api/projectphases/${phaseId}`,
    {
      method: 'PUT',
      body: JSON.stringify(req),
    }
  );
}

/**
 * Delete a phase.
 */
export async function deleteProjectPhase(
  phaseId: string
): Promise<void> {
  return apiFetch<void>(
    `/api/projectphases/${phaseId}`,
    { method: 'DELETE' }
  );
}
